import { useState } from 'react';
import { IonButton, IonIcon, IonInput, IonSpinner, IonText, useIonToast } from '@ionic/react';
import { closeOutline, createOutline } from 'ionicons/icons';
import { useMutation } from '@apollo/client/react';
import { CAREER_STATUS_MAX_LENGTH } from '../../../../appConfig';
import { UPDATE_CAREER_STATUS } from '../../../../graphql/mutations/careerHistory';
import type {
  UpdateCareerStatusMutation,
  UpdateCareerStatusMutationVariables,
} from '../../../../types/graphql-generated';

interface CareerStatusLineProps {
  careerStatus: string | null | undefined;
}

// Free-text career status shown under the user's name on the profile
// (e.g. "Jugador de Avilés Stadium"). It can be edited inline.
export const CareerStatusLine = ({ careerStatus }: CareerStatusLineProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState('');
  const [errors, setErrors] = useState<string[]>([]);
  const [presentToast] = useIonToast();

  const [updateCareerStatus, { loading: saving }] = useMutation<
    UpdateCareerStatusMutation,
    UpdateCareerStatusMutationVariables
  >(UPDATE_CAREER_STATUS);

  const startEditing = () => {
    setValue(careerStatus ?? '');
    setErrors([]);
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setErrors([]);
    setIsEditing(false);
  };

  const handleSave = async () => {
    try {
      const { data } = await updateCareerStatus({
        variables: { input: { careerStatus: value.trim() } },
      });
      const result = data?.updateCareerStatus;

      if (result?.userAccount && result.errors.length === 0) {
        presentToast({
          message: 'Estado de carrera actualizado',
          duration: 2500,
          position: 'bottom',
          positionAnchor: 'app-footer',
          cssClass: 'toast-success',
          buttons: [{ icon: closeOutline, role: 'cancel' }],
        });
        setIsEditing(false);
      } else {
        setErrors(result?.errors ?? ['No se ha podido guardar el estado de carrera']);
      }
    } catch (error) {
      console.error('Error updating career status:', error);
      setErrors(['No se ha podido guardar el estado de carrera. Inténtalo de nuevo más tarde.']);
    }
  };

  if (isEditing) {
    return (
      <div className="mt-3">
        <IonInput
          label="Estado de carrera"
          labelPlacement="floating"
          fill="outline"
          placeholder="Ej.: Jugador de Avilés Stadium"
          maxlength={CAREER_STATUS_MAX_LENGTH}
          counter
          value={value}
          onIonInput={(event) => setValue(event.detail.value?.toString() ?? '')}
          aria-label="Estado de carrera"
        />
        {errors.map((error) => (
          <IonText color="danger" key={error}>
            <p className="m-0 mt-1 text-sm">{error}</p>
          </IonText>
        ))}
        <div className="mt-2 flex justify-end gap-2">
          <IonButton fill="clear" size="small" onClick={cancelEditing} disabled={saving}>
            Cancelar
          </IonButton>
          <IonButton size="small" onClick={handleSave} disabled={saving}>
            {saving ? <IonSpinner name="crescent" /> : 'Guardar'}
          </IonButton>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-3 flex items-center justify-between gap-2">
      {careerStatus ? (
        <p className="m-0 min-w-0 flex-1 text-sm font-semibold text-slate-700">{careerStatus}</p>
      ) : (
        <p className="m-0 min-w-0 flex-1 text-sm text-slate-400">
          Añade el estado de carrera (por ejemplo "Entrenador del Avilés Stadium")
        </p>
      )}
      <IonButton
        fill="clear"
        size="small"
        onClick={startEditing}
        aria-label="Editar estado de carrera"
        data-testid="edit-career-status"
      >
        <IonIcon slot="icon-only" icon={createOutline} />
      </IonButton>
    </div>
  );
};
